import { convertMathJax, typesetMath } from 'modules/assessment/AssessmentApp';
import { loadMathJaxScript } from 'modules/assessment/loadMathJaxScript';
import React, { useState, useEffect, useRef } from 'react';

// --- Math Content Wrapper ---
const MathContent = React.memo(({ children, className, tag = 'p' }) => {
    const Tag = tag;
    return <Tag className={className}>{convertMathJax(children)}</Tag>;
});

// Refactored to use direct dark: classes
const getDifficultyColorClass = (difficulty) => {
    if (difficulty === 'E') return 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-200';
    if (difficulty === 'M') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-200';
    if (difficulty === 'H') return 'bg-red-100 text-red-800 dark:bg-red-700 dark:text-red-200';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
};

const shuffleArray = (array) => {
    if (!array || array.length === 0) return [];
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
};

// --- Image Carousel Component (Stub) ---
// isDarkMode prop removed and replaced with dark: classes
const ImageCarousel = React.memo(({ images }) => {
    return (
        // Replaced applyThemeClass with direct dark: classes
        <div className={`p-4 rounded-xl shadow-inner bg-gray-100 dark:bg-gray-700 flex flex-col items-center justify-center space-y-4 w-full h-full`}>
            {/* Replaced applyThemeClass with direct dark: classes */}
            <p className={`text-gray-600 dark:text-gray-400 italic`}>Image Carousel ({images?.length || 0} images)</p>
        </div>
    );
});


// --- Main Intuition Modal Component ---

export const IntuitionModal = React.memo(({
    selectedIF,
    isFactCompleted,
    UI_CONFIG,
    onClose,
    onCheckFact,
    // isDarkMode prop removed
}) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const [currentAssessmentIndex, setCurrentAssessmentIndex] = useState(0);
    const [shuffledAssessments, setShuffledAssessments] = useState([]);
    const [userAnswers, setUserAnswers] = useState([]);
    const [attemptScore, setAttemptScore] = useState({ correct: 0, total: 0, isPerfect: false });
    const [showResult, setShowResult] = useState(false);
    const [showImagePanel, setShowImagePanel] = useState(false);
    const [summaryIndex, setSummaryIndex] = useState(0);

    const fact = selectedIF;
    const hasImages = fact?.imageMaterials?.length > 0;

    // Ref for the content that changes and needs typesetting
    const modalContentRef = useRef();
    useEffect(() => {
        loadMathJaxScript();

        setTimeout(() => {
            typesetMath(modalContentRef.current);
        }, 500)
    }, [currentAssessmentIndex]);

    useEffect(() => {
        typesetMath(modalContentRef.current);

        if (selectedIF) {
            setIsFlipped(!!isFactCompleted);
            const originalAssessments = selectedIF.assessments || [];
            const newShuffledAssessments = shuffleArray(originalAssessments);
            setShuffledAssessments(newShuffledAssessments);

            setCurrentAssessmentIndex(0);
            const assessmentCount = newShuffledAssessments.length;
            setUserAnswers(new Array(assessmentCount).fill(null));
            setAttemptScore({ correct: 0, total: assessmentCount, isPerfect: false });
            setShowResult(false);
            setShowImagePanel(false);
            setSummaryIndex(0);
        }

    }, [selectedIF, isFactCompleted]);



    // if (!fact || !fact.assessments || fact.assessments.length === 0) return null;
    // if (shuffledAssessments.length === 0) return null;

    const currentAssessment = shuffledAssessments[currentAssessmentIndex];
    const currentSummaryAssessment = showResult ? shuffledAssessments[summaryIndex] : null;
    const currentSummaryAnswer = showResult ? userAnswers[summaryIndex] : null;

    const handleFlip = () => {
        setIsFlipped(prev => !prev);
    };

    const handleAnswer = (userGuess) => {
        if (!selectedIF || showResult) return;
        const assessment = shuffledAssessments[currentAssessmentIndex];
        const isCorrect = userGuess === assessment.expectedAnswer;
        const nextAnswers = [...userAnswers];
        nextAnswers[currentAssessmentIndex] = isCorrect;
        setUserAnswers(nextAnswers);
        const isLastQuestion = currentAssessmentIndex === shuffledAssessments.length - 1;

        if (isLastQuestion) {
            const finalCorrect = nextAnswers.filter(answer => answer).length;
            const total = shuffledAssessments.length;
            const isPerfect = finalCorrect === total;
            setAttemptScore({ correct: finalCorrect, total: total, isPerfect: isPerfect });
            setShowResult(true);
            if (isPerfect) {
                onCheckFact(selectedIF.id);
            }
            if (!isFlipped) handleFlip();
        } else {
            setCurrentAssessmentIndex(prev => prev + 1);
        }
    };

    // UI Classes - Refactored to use direct dark: classes
    const resultStatusClasses = attemptScore.isPerfect ? 'text-green-500' : 'text-red-500';
    const darkResultClasses = attemptScore.isPerfect ? 'dark:text-green-400' : 'dark:text-red-400';
    const modalBg = 'bg-white dark:bg-gray-800'; // Replaced applyThemeClass
    const backBg = 'bg-gray-50 dark:bg-gray-900'; // Replaced applyThemeClass
    const borderColor = 'border-gray-200 dark:border-gray-700'; // Replaced applyThemeClass
    const isImageVisible = hasImages && showImagePanel;
    const modalContentClasses = `w-11/12 max-h-[95vh] p-4 ${isImageVisible ? 'md:max-w-4xl' : 'md:max-w-xl'}`;
    const columnTitleClasses = `text-xl font-extrabold text-indigo-700 dark:text-indigo-300 border-b ${borderColor} pb-2 mb-4`;
    const toggleButtonClasses = `text-sm font-semibold px-3 py-1 rounded-full shadow-md transition whitespace-nowrap ${isImageVisible
        ? 'bg-red-500 text-white hover:bg-red-600'
        : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-800 dark:text-indigo-200 dark:hover:bg-indigo-700'
        }`;
    const toggleButtonText = isImageVisible ? 'Hide Visuals' : `Show Visuals (${fact?.imageMaterials?.length})`;

    return (

        <div id="intuition-modal" ref={modalContentRef} className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 backdrop-blur-sm" onClick={onClose}>
            <div
                id="modal-content-wrapper"
                // Attach ref for MathJax typesetting
                // Used modalBg (dark: class)
                className={`relative rounded-xl shadow-2xl ${modalBg} ${modalContentClasses} transition-all duration-500 ease-out scale-100`}
                onClick={e => e.stopPropagation()}
            >
                <div id="flip-container"
                    className={`relative w-full h-[40rem] transition-transform duration-700 ease-in-out transform-style-3d ${isFlipped ? 'rotate-y-180' : ''}`}
                    style={{ perspective: '1000px' }}>

                    {/* ================================================================
                    MODAL FRONT (Fact Content)
                    ================================================================ 
                    */}
                    <div id="modal-front" className={`absolute w-full h-full backface-hidden rounded-xl shadow-2xl ${modalBg} p-6 flex flex-col justify-between`}>
                        {/* Removed isDarkMode from close button */}
                        <button onClick={onClose} className={`absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-20`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>

                        <div className="flex justify-between items-start mb-3 pr-8 flex-shrink-0">
                            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Fact ID: <span id="modal-fact-id" className="text-indigo-600 dark:text-indigo-400">{fact.id}</span></p>
                            {hasImages && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowImagePanel(prev => !prev); }}
                                    className={toggleButtonClasses}
                                >
                                    {toggleButtonText}
                                </button>
                            )}
                        </div>

                        <div className="flex-grow flex flex-col md:flex-row min-h-0 space-y-6 md:space-y-0 md:space-x-6">

                            <div className={`flex flex-col min-h-0 ${isImageVisible ? 'w-full md:w-1/2' : 'w-full'} transition-all duration-300`}>
                                <h3 id="modal-title" className={columnTitleClasses}>Core Concept</h3>
                                <div className="overflow-y-auto pr-2 flex-grow">
                                    {/* *** MATH CONTENT: Fact Content *** */}
                                    <MathContent
                                        tag="p"
                                        // Added dark: class directly
                                        className={`text-gray-700 dark:text-gray-300 text-base leading-relaxed`}
                                    >
                                        {fact.fact}
                                    </MathContent>
                                </div>
                            </div>

                            {isImageVisible && (
                                <div className="w-full md:w-1/2 flex flex-col min-h-0">
                                    <h3 id="visual-title" className={columnTitleClasses}>Visuals</h3>
                                    <div className="overflow-y-auto pr-2 flex-grow">
                                        {/* Removed isDarkMode prop */}
                                        <ImageCarousel images={fact.imageMaterials} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                            {fact.description}
                        </p>

                        <div className={`flex justify-between items-end mt-4 pt-4 border-t ${borderColor} flex-shrink-0`}>
                            <span
                                id="modal-difficulty"
                                // Removed isDarkMode from utility call
                                className={`${getDifficultyColorClass(fact.difficulty.charAt(0))} text-xs font-bold px-3 py-1 rounded-full shadow-md`}
                            >
                                {fact.difficulty} ({fact.classLevel})
                            </span>
                            {shuffledAssessments.length > 0 && <button
                                onClick={() => {
                                    handleFlip();
                                    setShowImagePanel(false);
                                }}
                                id="flip-button"
                                className="px-4 py-2 bg-amber-500 text-white rounded-lg font-bold shadow-md hover:bg-amber-600 dark:hover:bg-amber-400 transition duration-200"
                            >
                                {UI_CONFIG.buttonText.flipCard}
                            </button>
                            }
                        </div>
                    </div>
                    {/* --- End Modal Front --- */}


                    {/* ================================================================
                    MODAL BACK (Assessment/Results)
                    ================================================================ 
                    */}
                    <div id="modal-back" className={`absolute w-full h-full backface-hidden rotate-y-180 rounded-xl shadow-2xl ${backBg} p-6 flex flex-col justify-between`}>
                        {/* Removed isDarkMode from close button */}
                        <button onClick={onClose} className={`absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-20`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>

                        {/* --- Question View --- */}
                        <div id="question-view" className={`flex flex-col h-full ${showResult ? 'hidden' : ''}`}>
                            <div className="flex justify-between items-start mb-3 pr-8 flex-shrink-0">
                                <h3 className={`text-xl font-extrabold text-indigo-700 dark:text-indigo-400 pb-2`}>
                                    {UI_CONFIG.flower.name} Check: Question {currentAssessmentIndex + 1} of {shuffledAssessments.length}
                                </h3>
                                {hasImages && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setShowImagePanel(prev => !prev); }}
                                        className={toggleButtonClasses}
                                    >
                                        {toggleButtonText}
                                    </button>
                                )}
                            </div>

                            <div className={`flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6 flex-grow min-h-0`}>
                                <div className={`flex flex-col justify-between h-full ${isImageVisible ? 'w-full md:w-1/2' : 'w-full'} transition-all duration-300`}>
                                    <div className="flex-grow flex flex-col justify-center min-h-0">
                                        <div className="overflow-y-auto pr-2 py-4 flex items-center justify-center flex-grow text-center">
                                            {/* *** MATH CONTENT: Question *** */}
                                            <MathContent
                                                tag="p"
                                                // Added dark: class directly
                                                className={`text-gray-800 dark:text-gray-200 text-lg font-semibold italic`}
                                            >
                                                {currentAssessment?.question}
                                            </MathContent>
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="flex flex-col space-y-3 md:flex-row md:space-x-4 md:space-y-0 mt-4">
                                            <button onClick={() => handleAnswer(true)} id="answer-true-button" className="w-full py-3 bg-green-500 text-white text-lg font-bold rounded-xl shadow-lg hover:bg-green-600 transition">
                                                {UI_CONFIG.buttonText.answerTrue}
                                            </button>
                                            <button onClick={() => handleAnswer(false)} id="answer-false-button" className="w-full py-3 bg-red-500 text-white text-lg font-bold rounded-xl shadow-lg hover:bg-red-600 transition">
                                                {UI_CONFIG.buttonText.answerFalse}
                                            </button>
                                        </div>
                                        {/* Added dark: class directly */}
                                        <button onClick={handleFlip} id="go-back-fact-button" className={`mt-4 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition duration-200`}>
                                            {UI_CONFIG.buttonText.goBackFact}
                                        </button>
                                    </div>
                                </div>

                                {isImageVisible && (
                                    <div className="w-full md:w-1/2 flex flex-col min-h-0 h-full">
                                        <h3 id="visual-title-q" className={columnTitleClasses}>Visuals</h3>
                                        <div className="overflow-y-auto pr-2 flex-grow">
                                            {/* Removed isDarkMode prop */}
                                            <ImageCarousel images={fact.imageMaterials} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* --- Result View --- */}
                        <div id="result-view" className={`flex flex-col h-full ${showResult ? '' : 'hidden'}`}>
                            <div className="flex justify-between items-start mb-3 pr-8 flex-shrink-0">
                                <h3 id="result-status" className={`text-2xl font-black ${resultStatusClasses} ${darkResultClasses}`}>
                                    {attemptScore.isPerfect ? UI_CONFIG.resultText.correctStatus : UI_CONFIG.resultText.incorrectStatus}
                                </h3>
                                {hasImages && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setShowImagePanel(prev => !prev); }}
                                        className={toggleButtonClasses}
                                    >
                                        {toggleButtonText}
                                    </button>
                                )}
                            </div>

                            <div className={`flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6 flex-grow min-h-0`}>
                                <div className={`flex flex-col justify-between h-full ${isImageVisible ? 'w-full md:w-1/2' : 'w-full'} transition-all duration-300`}>
                                    <div className="flex-grow flex flex-col min-h-0 space-y-4">
                                        <div className="flex-grow space-y-4 overflow-y-auto pr-2">
                                            {/* Replaced applyThemeClass with direct dark: classes */}
                                            <h4 className={`text-xl font-bold text-gray-800 dark:text-gray-200 mb-2`}>Summary: {attemptScore.correct}/{attemptScore.total} Correct</h4>

                                            <div className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
                                                {/* *** MATH CONTENT: Review Question *** */}
                                                {/* Replaced applyThemeClass with direct dark: classes */}
                                                <MathContent tag="p" className={`font-bold text-gray-800 dark:text-gray-200`}>
                                                    Q{summaryIndex + 1} of {shuffledAssessments.length}: {currentSummaryAssessment?.question}
                                                </MathContent>
                                                {/* dark: classes already present */}
                                                <p className={`text-sm mt-1 font-semibold ${currentSummaryAnswer ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                    Your Answer: {currentSummaryAnswer === true ? 'TRUE' : currentSummaryAnswer === false ? 'FALSE' : 'Not Answered'}
                                                    <span className="ml-2 font-normal">
                                                        ({currentSummaryAnswer === currentSummaryAssessment?.expectedAnswer ? 'Correct' : 'Incorrect'})
                                                    </span>
                                                </p>
                                            </div>

                                            <div className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm">
                                                {/* Replaced applyThemeClass with direct dark: classes */}
                                                <p className={`font-bold text-gray-700 dark:text-gray-300`}>Rationale:</p>
                                                {/* *** MATH CONTENT: Rationale *** */}
                                                {/* Replaced applyThemeClass with direct dark: classes */}
                                                <MathContent tag="p" className={`text-sm text-gray-600 dark:text-gray-400 mt-1`}>
                                                    {currentSummaryAssessment?.rationale}
                                                </MathContent>
                                            </div>
                                            <div className="p-3 bg-yellow-50 dark:bg-yellow-900 border border-yellow-300 dark:border-yellow-700 rounded-lg shadow-sm">
                                                {/* dark: classes already present */}
                                                <p className="font-bold text-yellow-800 dark:text-yellow-400">Common Misconception:</p>
                                                {/* dark: classes already present */}
                                                <MathContent tag="p" className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                                    {currentSummaryAssessment?.misconception}
                                                </MathContent>
                                            </div>

                                            {/* Summary Navigation (dark: classes already present) */}
                                            <div className="flex justify-between items-center mt-4">
                                                <button
                                                    onClick={() => setSummaryIndex(prev => prev - 1)}
                                                    disabled={summaryIndex === 0}
                                                    className={`px-4 py-2 rounded-lg font-bold transition ${summaryIndex === 0 ? 'bg-gray-300 text-gray-500 cursor-default' : 'bg-indigo-500 text-white hover:bg-indigo-600 dark:bg-indigo-700 dark:hover:bg-indigo-600'}`}
                                                >
                                                    Previous
                                                </button>
                                                {/* Replaced applyThemeClass with direct dark: classes */}
                                                <span className={`text-sm text-gray-600 dark:text-gray-400`}>Reviewing {summaryIndex + 1} of {shuffledAssessments.length}</span>
                                                <button
                                                    onClick={() => setSummaryIndex(prev => prev + 1)}
                                                    disabled={summaryIndex === shuffledAssessments.length - 1}
                                                    className={`px-4 py-2 rounded-lg font-bold transition ${summaryIndex === shuffledAssessments.length - 1 ? 'bg-gray-300 text-gray-500 cursor-default' : 'bg-indigo-500 text-white hover:bg-indigo-600 dark:bg-indigo-700 dark:hover:bg-indigo-600'}`}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`mt-4 pt-4 border-t ${borderColor} flex justify-between items-center flex-shrink-0`}>
                                        <button onClick={onClose} id="back-to-leaves-button" className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold shadow-md hover:bg-indigo-700 transition duration-200">
                                            {UI_CONFIG.buttonText.backToLeaves}
                                        </button>
                                        {/* Added dark: class directly */}
                                        <button onClick={handleFlip} id="review-fact-button" className={`text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition duration-200`}>
                                            {UI_CONFIG.buttonText.goBackFact}
                                        </button>
                                    </div>
                                </div>

                                {isImageVisible && (
                                    <div className="w-full md:w-1/2 flex flex-col min-h-0 h-full">
                                        <h3 id="visual-title-r" className={columnTitleClasses}>Visuals</h3>
                                        <div className="overflow-y-auto pr-2 flex-grow">
                                            {/* Removed isDarkMode prop */}
                                            <ImageCarousel images={fact.imageMaterials} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* --- End Modal Back --- */}
                </div>
            </div>
        </div>
    );
});